import { byFieldStringComparator, memoizeReduxState, pluck } from '@range.io/functional'
import CollaborationSelectors from './persisted-state/collaboration-slice/collaboration-selectors.js'
import filterCollaborations from './redux-filter-common.js'

const _filteredCollaborations = state => {
    const isTask = cs => cs.isTask
    const isNotTask = cs => !cs.isTask

    const sort = collaborations => {
        if (sortOrder === 'recent') return collaborations.sort((a, b) => b.lastUpdateDate - a.lastUpdateDate)
        if (sortOrder === 'alphabetical') return collaborations.sort(byFieldStringComparator('name', false, true))

        throw new Error("Don't understand sortOrder")
    }

    const { navigatorFilterSettings, navigatorSortSettings, selectedCanvas, selectedUserId } = state
    const { showPhotoPins, showTaskPins } = navigatorFilterSettings
    const { sortOrder } = navigatorSortSettings

    if (!selectedCanvas) return [] // too early

    let collaborations = CollaborationSelectors.collaborationsAsLookupTable(state)

    // Apply photo/task pin filters specific to canvas view
    if (!showPhotoPins) collaborations = collaborations.filter(isTask)
    if (!showTaskPins) collaborations = collaborations.filter(isNotTask)

    // Apply common filters
    collaborations = filterCollaborations(collaborations, navigatorFilterSettings, selectedUserId)

    return sort(collaborations)
}

/*
 * Memoize the _filteredCollaborations function, recomputing only if any of the collections or filters has changed
 */
const filteredCollaborations = memoizeReduxState(
    [
        'navigatorFilterSettings', // the whole point...
        'navigatorSortSettings', // the whole point...

        'canvases',
        'collaborations',
        'geometries',
        'selectedCanvas',
        'uploads', // we need this so when upload image URLs get updated this also gets refreshed
        'statusNames',
    ],
    _filteredCollaborations
)

/*
 * Return ONLY the geometries that meet the user's filter criteria (on the current canvas)
 * @sig _geometriesForSelectedCanvas :: State -> [Geometry]
 *
 */
const _geometriesForSelectedCanvas = state => {
    let collaborations = filteredCollaborations(state)
    collaborations = collaborations.filter(cs => cs.canvasId === state.selectedCanvas)
    const geometryIds = pluck('geometryId', collaborations)
    return geometryIds.map(id => state.geometries[id])
}

/*
 * It's expensive to call _geometriesForSelectedCanvas, and it changes "rarely" and each call with the same
 * inputs will return a *different* array filled with the same Geometries, so memoize!
 *
 * Note/hack:
 * 'collaborations' is in this list because when a Geometry is added, the collaboration will possibly
 * have arrived earlier, in which case the Collaboration's canvas will not be around yet, because
 * the geometry has the canvas. We could fix this a couple of ways, including duplicating the canvas id in Firestore.
 * Because the Collaboration has no canvas, it is, by definition, not on the "current" canvas and never appears.
 * However, as it happens, we will definitely get storageVersion updates for a new collaboration AFTER we
 * have gotten the Collaboration's Geometry (and therefore its canvas) when the location snapshot arrives,
 * at which time we also have a canvas for the Collaboration, and it will appear.
 * DroneDeploy maps will not trigger updates, because we currently don't create locationSnapshots for these
 */
const geometriesForSelectedCanvas = memoizeReduxState(
    ['selectedCanvas', 'navigatorFilterSettings', 'geometries', 'collaborations'],
    _geometriesForSelectedCanvas
)

export { filteredCollaborations, geometriesForSelectedCanvas }
